import { DispatchCandidateSortingType } from "@src/shipments/types";
import {
  dispatchDriverLabelFilterStorageKey,
  useOptionalShipmentFiltersContext,
} from "@src/shipments/context/ShipmentFiltersContext";
import React, { createContext, useContext, useState, ReactNode } from "react";
import { useLocalStorage } from "usehooks-ts";
import { DispatchCandidateView } from "../DispatchCandidatesWidget";
import { useAuth } from "@src/common/hooks/useAuth";

interface CandidatesContextProps {
  candidates: DispatchCandidateView[];
  setCandidates: React.Dispatch<React.SetStateAction<DispatchCandidateView[]>>;
  excludedCandidates: DispatchCandidateView[];
  setExcludedCandidates: React.Dispatch<
    React.SetStateAction<DispatchCandidateView[]>
  >;
  unavailableCandidates: DispatchCandidateView[];
  setUnavailableCandidates: React.Dispatch<
    React.SetStateAction<DispatchCandidateView[]>
  >;
  specializedCandidates: DispatchCandidateView[];
  setSpecializedCandidates: React.Dispatch<
    React.SetStateAction<DispatchCandidateView[]>
  >;
  dispatchCandidatesSortingType: DispatchCandidateSortingType;
  setDispatchCandidatesSortingType: React.Dispatch<
    React.SetStateAction<DispatchCandidateSortingType>
  >;
  isDistanceFilterEnabled: boolean;
  setIsDistanceFilterEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isVehicleTypeFilterEnabled: boolean;
  setIsVehicleTypeFilterEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isWeightFilterEnabled: boolean;
  setIsWeightFilterEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isShowDriverLabelsEnabled: boolean;
  setIsShowDriverLabelsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isShowDriverPayEnabled: boolean;
  setIsShowDriverPayEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isShowDailyEarningsEnabled: boolean;
  setIsShowDailyEarningsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDriverLabelIds: string[];
  setSelectedDriverLabelIds: (labelIds: string[]) => void;
}

const CandidatesContext =
  createContext<CandidatesContextProps | undefined>(undefined);

export const CandidatesProvider = ({ children }: { children: ReactNode }) => {
  const { courierId } = useAuth();
  const [candidates, setCandidates] = useState<DispatchCandidateView[]>([]);
  const [excludedCandidates, setExcludedCandidates] = useState<
    DispatchCandidateView[]
  >([]);
  const [unavailableCandidates, setUnavailableCandidates] = useState<
    DispatchCandidateView[]
  >([]);
  const [specializedCandidates, setSpecializedCandidates] = useState<
    DispatchCandidateView[]
  >([]);
  const [dispatchCandidatesSortingType, setDispatchCandidatesSortingType] =
    useLocalStorage<DispatchCandidateSortingType>(
      "MonitorPageDispatchCandidateSortingType",
      DispatchCandidateSortingType.LEAST_DEVIATION
    );
  const [isDistanceFilterEnabled, setIsDistanceFilterEnabled] =
    useLocalStorage<boolean>("DispatchCandidateDistanceFilter", true);
  const [isVehicleTypeFilterEnabled, setIsVehicleTypeFilterEnabled] =
    useLocalStorage<boolean>("DispatchCandidateVehicleTypeFilter", true);
  const [isWeightFilterEnabled, setIsWeightFilterEnabled] = useLocalStorage<
    boolean
  >("DispatchCandidateWeightFilter", true);
  const [isShowDriverLabelsEnabled, setIsShowDriverLabelsEnabled] =
    useLocalStorage<boolean>("DispatchCandidateShowDriverLabels", false);
  const [isShowDriverPayEnabled, setIsShowDriverPayEnabled] =
    useLocalStorage<boolean>("DispatchCandidateShowDriverPay", false);
  const [isShowDailyEarningsEnabled, setIsShowDailyEarningsEnabled] =
    useLocalStorage<boolean>("DispatchCandidateShowDailyEarnings", false);
  // On the orders page the driver-label filter lives in ShipmentFiltersContext so
  // it can be saved per dispatch view; elsewhere (Fleet page) fall back to the
  // same courier-scoped localStorage key the context persists to (CRI-166).
  const shipmentFilters = useOptionalShipmentFiltersContext();
  const [localDriverLabelIds, setLocalDriverLabelIds] = useLocalStorage<
    string[]
  >(dispatchDriverLabelFilterStorageKey(courierId), []);
  const selectedDriverLabelIds =
    shipmentFilters?.dispatchDriverLabelIds ?? localDriverLabelIds;
  const setSelectedDriverLabelIds = shipmentFilters
    ? shipmentFilters.setDispatchDriverLabelIds
    : setLocalDriverLabelIds;

  return (
    <CandidatesContext.Provider
      value={{
        candidates,
        setCandidates,
        excludedCandidates,
        setExcludedCandidates,
        unavailableCandidates,
        setUnavailableCandidates,
        specializedCandidates,
        setSpecializedCandidates,
        dispatchCandidatesSortingType,
        setDispatchCandidatesSortingType,
        isDistanceFilterEnabled,
        setIsDistanceFilterEnabled,
        isVehicleTypeFilterEnabled,
        setIsVehicleTypeFilterEnabled,
        isWeightFilterEnabled,
        setIsWeightFilterEnabled,
        isShowDriverLabelsEnabled,
        setIsShowDriverLabelsEnabled,
        isShowDriverPayEnabled,
        setIsShowDriverPayEnabled,
        isShowDailyEarningsEnabled,
        setIsShowDailyEarningsEnabled,
        selectedDriverLabelIds,
        setSelectedDriverLabelIds,
      }}
    >
      {children}
    </CandidatesContext.Provider>
  );
};

export const useCandidates = () => {
  const context = useContext(CandidatesContext);
  if (context === undefined) {
    throw new Error("useCandidates must be used within a CandidatesProvider");
  }
  return context;
};
